import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Heart, MapPin, Bed, Bath, Square, CheckCircle2, Navigation } from 'lucide-react';
import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { formatOriginalPrice, formatPriceWithConversion, BILLING_PERIODS } from '../utils/currency';
import { formatDistance } from '../utils/geolocation';

const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

const propertyTypeLabels = {
  room: 'Room',
  flat: 'Flat',
  apartment: 'Apartment',
  house: 'House',
  villa: 'Villa',
  studio: 'Studio',
  shared_room: 'Shared Room',
  shop: 'Shop',
  office: 'Office',
  commercial: 'Commercial',
};

const defaultImages = [
  'https://images.unsplash.com/photo-1502672260266-1c1ef2d93688?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1560448204-e02f11c3d0e2?auto=format&fit=crop&w=800&q=80',
  'https://images.unsplash.com/photo-1484154218962-a197022b5858?auto=format&fit=crop&w=800&q=80',
];

export default function PropertyCard({ property, onFavoriteChange, viewerCurrency = 'USD', showDistance = false }) {
  const { isAuthenticated } = useAuth();
  const [isFavorite, setIsFavorite] = useState(property.is_favorite || false);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [imgLoaded, setImgLoaded] = useState(false);
  const [heartBurst, setHeartBurst] = useState(0);

  const coverImage = property.media?.[0]?.url || defaultImages[Math.floor(Math.random() * defaultImages.length)];

  // Get price formatting with original currency (source of truth) + optional conversion
  const originalCurrency = property.currency || 'USD';
  const billingPeriod = property.billing_period || 'monthly';
  const priceInfo = formatPriceWithConversion(
    property.monthly_rent,
    originalCurrency,
    viewerCurrency,
    billingPeriod
  );

  // Format deposit in original currency
  const formatDeposit = (amount, currency) => {
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency,
        maximumFractionDigits: 0,
      }).format(amount);
    } catch {
      return `${currency} ${amount.toLocaleString()}`;
    }
  };

  const handleFavorite = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      toast.error('Please sign in to save favorites');
      return;
    }

    setFavoriteLoading(true);
    setHeartBurst((n) => n + 1);
    try {
      const method = isFavorite ? 'DELETE' : 'POST';
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/api/favorites/${property.property_id}`, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        setIsFavorite(!isFavorite);
        toast.success(isFavorite ? 'Removed from favorites' : 'Added to favorites');
        onFavoriteChange?.(!isFavorite);
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    } finally {
      setFavoriteLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 14 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.2 }}
      transition={{ duration: 0.5, ease: [0.32, 0.72, 0, 1] }}
      whileHover={{ y: -6 }}
      className="will-change-transform"
    >
      <Link
        to={`/property/${property.property_id}`}
        className="card-property group block"
        data-testid={`property-card-${property.property_id}`}
      >
        {/* Image */}
        <div className="relative aspect-[4/3] overflow-hidden">
          {/* Shimmer placeholder while image loads */}
          <AnimatePresence>
            {!imgLoaded && (
              <motion.div
                key="shimmer"
                initial={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.4 }}
                className="absolute inset-0 skeleton-shimmer"
              />
            )}
          </AnimatePresence>

          <motion.img
            src={coverImage}
            alt={property.title}
            loading="lazy"
            onLoad={() => setImgLoaded(true)}
            initial={{ scale: 1.08, opacity: 0 }}
            animate={{ scale: imgLoaded ? 1 : 1.08, opacity: imgLoaded ? 1 : 0 }}
            transition={{ duration: 0.7, ease: [0.25, 0.1, 0.25, 1] }}
            className="h-full w-full object-cover transition-transform [transition-duration:900ms] ease-luxury group-hover:scale-[1.08]"
          />

          {/* Soft gradient wash — deepens slightly on hover for depth */}
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-950/30 via-transparent to-transparent opacity-60 transition-opacity duration-500 group-hover:opacity-90" />

          {/* Favorite Button */}
          <motion.button
            onClick={handleFavorite}
            disabled={favoriteLoading}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.85 }}
            animate={heartBurst ? { scale: [1, 1.25, 1] } : {}}
            transition={{ duration: 0.35, ease: [0.34, 1.56, 0.64, 1] }}
            className={`absolute top-4 right-4 w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-300 shadow-md ${
              isFavorite
                ? 'bg-accent text-white'
                : 'bg-white/90 backdrop-blur-sm text-slate-600 hover:bg-white'
            }`}
            data-testid={`favorite-btn-${property.property_id}`}
            aria-label={isFavorite ? 'Remove from favorites' : 'Save to favorites'}
          >
            <Heart className={`w-5 h-5 transition-all duration-300 ${isFavorite ? 'fill-current' : ''}`} />
          </motion.button>

          {/* Badges */}
          <div className="absolute top-4 left-4 flex gap-2">
            {property.owner_verified && (
              <Badge className="bg-emerald-500 text-white border-0 shadow-sm">
                <CheckCircle2 className="w-3 h-3 mr-1" />
                Verified
              </Badge>
            )}
            <Badge variant="secondary" className="bg-white/90 backdrop-blur-sm text-slate-700 border-0 shadow-sm">
              {propertyTypeLabels[property.property_type] || property.property_type}
            </Badge>
          </div>

          {/* Price Tag — rises slightly on hover for a tactile feel */}
          <motion.div
            initial={{ y: 0 }}
            whileHover={{ y: -2 }}
            className="absolute bottom-4 left-4"
          >
            <div className="bg-white/95 backdrop-blur-sm rounded-xl px-4 py-2 shadow-lg ring-1 ring-black/5 transition-all duration-300 group-hover:shadow-xl">
              <div className="font-bold text-slate-900 text-lg leading-tight">
                {priceInfo.original}
              </div>
              {priceInfo.showConversion && (
                <div className="text-xs text-slate-500">
                  {priceInfo.converted}
                </div>
              )}
            </div>
          </motion.div>

          {/* Distance Badge */}
          {showDistance && property.distance !== null && property.distance !== undefined && (
            <div className="absolute bottom-4 right-4">
              <div className="bg-blue-500 text-white rounded-full px-3 py-1 text-xs font-medium flex items-center gap-1 shadow-lg">
                <Navigation className="w-3 h-3" />
                {formatDistance(property.distance)}
              </div>
            </div>
          )}
        </div>

        {/* Content */}
        <div className="p-5">
          <h3 className="font-serif text-lg font-bold text-slate-900 mb-2 line-clamp-1 transition-colors duration-300 group-hover:text-accent">
            {property.title}
          </h3>

          <div className="flex items-center gap-1 text-slate-500 mb-4">
            <MapPin className="w-4 h-4 flex-shrink-0" />
            <span className="text-sm line-clamp-1">
              {[property.location?.area, property.location?.city, property.location?.country]
                .filter(Boolean)
                .join(', ') || 'Location not specified'}
            </span>
          </div>

          {/* Property Details */}
          <div className="flex items-center gap-4 text-sm text-slate-600">
            {property.details?.bedroom_count > 0 && (
              <div className="flex items-center gap-1.5 transition-colors duration-300 group-hover:text-slate-900">
                <Bed className="w-4 h-4" />
                <span>{property.details.bedroom_count} {property.details.bedroom_count === 1 ? 'Bed' : 'Beds'}</span>
              </div>
            )}
            {property.details?.bathroom_count > 0 && (
              <div className="flex items-center gap-1.5 transition-colors duration-300 group-hover:text-slate-900">
                <Bath className="w-4 h-4" />
                <span>{property.details.bathroom_count} {property.details.bathroom_count === 1 ? 'Bath' : 'Baths'}</span>
              </div>
            )}
            {property.details?.area_sqft > 0 && (
              <div className="flex items-center gap-1.5 transition-colors duration-300 group-hover:text-slate-900">
                <Square className="w-4 h-4" />
                <span>{property.details.area_sqft} sqft</span>
              </div>
            )}
          </div>

          {/* Deposit */}
          {property.deposit > 0 && (
            <p className="mt-3 text-sm text-slate-500">
              Deposit: {formatDeposit(property.deposit, originalCurrency)}
              {property.negotiable && <span className="ml-2 text-emerald-600">(Negotiable)</span>}
            </p>
          )}
        </div>

        {/* Accent underline that slides in on hover */}
        <div className="h-[3px] w-0 bg-gradient-to-r from-accent via-rose-500 to-pink-500 transition-all duration-500 ease-luxury group-hover:w-full" />
      </Link>
    </motion.div>
  );
}
